import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrainIcon from '@mui/icons-material/Train';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import API from '../api/axios';

const RailwayBookingPopup = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    phone: '',
    bookingDate: '',
    numberOfPeople: 1,
    fromStation: '',
    toStation: '',
    trainClass: 'Sleeper',
    specialRequirements: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const color = '#0C2340';
  const gradient = 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.userName || !formData.email || !formData.phone || !formData.bookingDate || !formData.fromStation || !formData.toStation) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      await API.post('/bookings', {
        userName: formData.userName,
        email: formData.email,
        phone: formData.phone,
        bookingDate: formData.bookingDate,
        serviceType: 'Indian Railway Booking',
        mealPreference: 'No Preference',
        numberOfPeople: parseInt(formData.numberOfPeople) || 1,
        fromLocation: formData.fromStation,
        toLocation: formData.toStation,
        travelClass: formData.trainClass,
        specialRequirements: formData.specialRequirements,
      });

      setSnackbar({ open: true, message: 'Railway booking request submitted successfully!', severity: 'success' });
      setFormData({
        userName: '', email: '', phone: '', bookingDate: '', numberOfPeople: 1,
        fromStation: '', toStation: '', trainClass: 'Sleeper', specialRequirements: '',
      });
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));
  const todayStr = new Date().toISOString().split('T')[0];

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '&:hover fieldset': { borderColor: color },
      '&.Mui-focused fieldset': { borderColor: color, borderWidth: '2px' },
    },
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.35)' } }}>
        <Box sx={{ background: gradient, color: 'white', p: { xs: 2.5, md: 4 }, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', filter: 'blur(20px)' }} />
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
              <TrainIcon sx={{ fontSize: 40 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Book Your Train</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Reserve your railway journey across India</Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: { xs: 2.5, md: 4 } }}>
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" name="userName" value={formData.userName} onChange={handleChange} required size="small" sx={inputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: alpha(color, 0.5), fontSize: 18 }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required size="small" sx={inputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: alpha(color, 0.5), fontSize: 18 }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} required size="small" sx={inputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: alpha(color, 0.5), fontSize: 18 }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Journey Date" name="bookingDate" type="date" value={formData.bookingDate} onChange={handleChange} required size="small"
                  InputLabelProps={{ shrink: true }} inputProps={{ min: todayStr }} sx={inputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ color: alpha(color, 0.5), fontSize: 18 }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="From Station" name="fromStation" value={formData.fromStation} onChange={handleChange} required size="small" placeholder="e.g. Mumbai Central" sx={inputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: alpha(color, 0.5), fontSize: 18 }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="To Station" name="toStation" value={formData.toStation} onChange={handleChange} required size="small" placeholder="e.g. New Delhi" sx={inputSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ color: alpha(color, 0.5), fontSize: 18 }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Train Class" name="trainClass" value={formData.trainClass} onChange={handleChange} size="small" sx={inputSx}>
                  <MenuItem value="Sleeper">Sleeper (SL)</MenuItem>
                  <MenuItem value="3AC">3 Tier AC (3A)</MenuItem>
                  <MenuItem value="2AC">2 Tier AC (2A)</MenuItem>
                  <MenuItem value="1AC">1st Class AC (1A)</MenuItem>
                  <MenuItem value="CC">Chair Car (CC)</MenuItem>
                  <MenuItem value="EC">Executive Chair Car (EC)</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Passengers" name="numberOfPeople" type="number" value={formData.numberOfPeople} onChange={handleChange} size="small"
                  InputProps={{ inputProps: { min: 1, max: 50 } }} sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Special Requests (Optional)" name="specialRequirements" value={formData.specialRequirements}
                  onChange={handleChange} placeholder="Any special requests or preferences..." size="small" sx={inputSx} />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '10px', px: 4, py: 1.2, textTransform: 'none', fontWeight: 600, borderColor: alpha(color, 0.3), color, '&:hover': { borderColor: color, bgcolor: alpha(color, 0.04) } }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting} sx={{
                borderRadius: '10px', px: 4, py: 1.2, textTransform: 'none', fontWeight: 700,
                background: gradient, boxShadow: `0 8px 25px ${alpha(color, 0.35)}`,
                '&:hover': { boxShadow: `0 12px 35px ${alpha(color, 0.45)}`, transform: 'translateY(-1px)' },
                '&:disabled': { background: alpha(color, 0.3), boxShadow: 'none' },
              }}>
                {submitting ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} sx={{ color: 'white' }} /><span>Submitting...</span></Box> : 'Book Train'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default RailwayBookingPopup;